import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, PhoneCall, DollarSign, Mountain, Truck, Zap, MapPin, ArrowRight, Phone, ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
// FAQ Component
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-stone-200 dark:border-white/10 last:border-0" data-id="element-2749">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between py-5 text-left font-bold text-brand-charcoal dark:text-white hover:text-brand-orange transition-colors" data-id="element-2750">
        <span className="text-lg" data-id="element-2751">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2752" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-2753">
        <p className="pb-5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2754">
          {answer}
        </p>
      </motion.div>
    </div>;
}
export function LocalVsNational() {
  const t = useT();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const comparisonData = [{
    attribute: t('lvn.adv.1.attr'),
    local: t('lvn.adv.1.local'),
    national: t('lvn.adv.1.nat')
  }, {
    attribute: t('lvn.adv.2.attr'),
    local: t('lvn.adv.2.local'),
    national: t('lvn.adv.2.nat')
  }, {
    attribute: t('lvn.adv.3.attr'),
    local: t('lvn.adv.3.local'),
    national: t('lvn.adv.3.nat')
  }, {
    attribute: t('lvn.adv.4.attr'),
    local: t('lvn.adv.4.local'),
    national: t('lvn.adv.4.nat')
  }, {
    attribute: t('lvn.adv.5.attr'),
    local: t('lvn.adv.5.local'),
    national: t('lvn.adv.5.nat')
  }, {
    attribute: t('lvn.adv.6.attr'),
    local: t('lvn.adv.6.local'),
    national: t('lvn.adv.6.nat')
  }, {
    attribute: t('lvn.adv.7.attr'),
    local: t('lvn.adv.7.local'),
    national: t('lvn.adv.7.nat')
  }];
  const benefits = [{
    title: t('lvn.why.1.title'),
    desc: t('lvn.why.1.desc'),
    icon: PhoneCall
  }, {
    title: t('lvn.why.2.title'),
    desc: t('lvn.why.2.desc'),
    icon: DollarSign
  }, {
    title: t('lvn.why.3.title'),
    desc: t('lvn.why.3.desc'),
    icon: Mountain
  }, {
    title: t('lvn.why.4.title'),
    desc: t('lvn.why.4.desc'),
    icon: Truck
  }, {
    title: t('lvn.why.5.title'),
    desc: t('lvn.why.5.desc'),
    icon: Zap
  }, {
    title: t('lvn.why.6.title'),
    desc: t('lvn.why.6.desc'),
    icon: MapPin
  }];
  return <div className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen transition-colors duration-300" data-id="element-2755">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-2756">
        <div className="absolute inset-0 z-0" data-id="element-2757">
          <img src="https://cdn.magicpatterns.com/uploads/824wZoPcAij7yeGBiywpRR/file_00000000e00c722f85d5870ff5890903.png" alt="Dump truck delivery" className="w-full h-full object-cover" data-id="element-2758" />
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80" data-id="element-2759" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2760">
          <div className="max-w-3xl" data-id="element-2761">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1.5 px-4 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-2762">
              {t('lvn.hero.eyebrow')}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 leading-tight" data-id="element-2763">
              {t('lvn.hero.title1')} <br data-id="element-2764" />
              <span className="text-brand-orange" data-id="element-2765">{t('lvn.hero.title2')}</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-300 leading-relaxed mb-8 font-medium font-sub" data-id="element-2766">
              {t('lvn.hero.body')}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-2767">
              <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2768">
                {t('lvn.hero.btnQuote')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-2769" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-bold text-base sm:text-lg transition-colors" data-id="element-2770">
                <Phone className="w-5 h-5 mr-2" data-id="element-2771" />
                {t('lvn.hero.btnCall')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2772">
        <div className="text-center mb-16" data-id="element-2773">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2774">
            {t('lvn.advantage.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2775"></div>
          <p className="text-lg text-stone-600 dark:text-stone-300 max-w-2xl mx-auto font-sub" data-id="element-2776">
            {t('lvn.advantage.subtitle')}
          </p>
        </div>

        {/* Unified comparison list — no table chrome, no pill-styled
            sub-labels. One row per attribute: small attribute label up top,
            then a two-column side-by-side answer (local | national) below.
            Works the same way on mobile and desktop. */}
        <div className="max-w-5xl mx-auto" data-id="element-2777">
          {/* Compact column header — just two readable labels, no pills,
           no table TH. The "vs." mid-divider clarifies the relationship
           without dressing it up as table chrome. */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-baseline pb-4 mb-6 border-b border-stone-200 dark:border-white/10" data-id="element-2778">
            <div data-id="element-2779">
              <p className="text-[10px] sm:text-xs font-bold text-brand-orange uppercase tracking-[0.15em]" data-id="element-2780">
                253Gravel
              </p>
              <p className="text-sm sm:text-base font-extrabold text-brand-charcoal dark:text-white leading-tight mt-0.5" data-id="element-2781">
                {t('lvn.advantage.th2')}
              </p>
            </div>
            <span className="text-xs sm:text-sm font-bold text-stone-400 dark:text-stone-500 italic px-1 sm:px-3" data-id="element-2782">
              vs.
            </span>
            <div data-id="element-2783">
              <p className="text-[10px] sm:text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-[0.15em]" data-id="element-2784">
                The Alternative
              </p>
              <p className="text-sm sm:text-base font-extrabold text-stone-500 dark:text-stone-400 leading-tight mt-0.5" data-id="element-2785">
                {t('lvn.advantage.th3')}
              </p>
            </div>
          </div>

          {/* Comparison rows. Each row: attribute label, then local vs.
           national answer side-by-side. Subtle row dividers, no nested
           card chrome. */}
          <div className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2786">
            {comparisonData.map((row, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04,
            ease: 'easeOut'
          }} className="py-5 sm:py-6" data-id="element-2787">
                {/* Attribute label */}
                <p className="text-[11px] sm:text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-[0.12em] mb-3" data-id="element-2788">
                  {row.attribute}
                </p>
                {/* Side-by-side answers */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-start" data-id="element-2789">
                  <div className="flex items-start gap-2 sm:gap-3" data-id="element-2790">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2791">
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600 dark:text-green-400" data-id="element-2792" />
                    </div>
                    <span className="text-sm sm:text-base font-semibold text-brand-charcoal dark:text-white leading-snug" data-id="element-2793">
                      {row.local}
                    </span>
                  </div>
                  {/* Vertical divider — sits in the middle column on all sizes */}
                  <div className="w-px self-stretch bg-stone-200 dark:bg-white/10" aria-hidden="true" data-id="element-2794" />
                  <div className="flex items-start gap-2 sm:gap-3 opacity-70" data-id="element-2795">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2796">
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2797" />
                    </div>
                    <span className="text-sm sm:text-base font-medium dark:text-stone-400 leading-snug text-[#B9B4B1]" data-id="element-2798">
                      {row.national}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pullquote Band */}
      <section className="py-24 bg-brand-charcoal dark:bg-black text-center px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-2799">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png')] bg-cover bg-center opacity-10 mix-blend-luminosity" data-id="element-2800" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-2801">
          <Mountain className="w-12 h-12 text-brand-orange mx-auto mb-8 opacity-80" data-id="element-2802" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-relaxed mb-8" data-id="element-2803">
            {t('lvn.quote')}
          </h2>
          <div className="w-24 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2804"></div>
        </div>
      </section>

      {/* Benefit Deep Dive */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2805">
        <div className="text-center mb-16" data-id="element-2806">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2807">
            {t('lvn.why.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2808"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-2809">
          {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-white dark:bg-[#2a2a2a] p-8 rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow" data-id="element-2810">
              <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6" data-id="element-2811">
                <benefit.icon className="w-6 h-6 text-brand-orange" data-id="element-2812" />
              </div>
              <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3 font-heading" data-id="element-2813">
                {benefit.title}
              </h3>
              <p className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2814">
                {benefit.desc}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-white/5" data-id="element-2815">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2816">
          <div className="text-center mb-12" data-id="element-2817">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2818">
              {t('lvn.faq.title')}
            </h2>
            <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2819"></div>
          </div>

          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-6 md:p-8 shadow-sm" data-id="element-2820">
            <FaqItem question={t('lvn.faq.1.q')} answer={t('lvn.faq.1.a')} data-id="element-2821" />
            <FaqItem question={t('lvn.faq.2.q')} answer={t('lvn.faq.2.a')} data-id="element-2822" />
            <FaqItem question={t('lvn.faq.3.q')} answer={t('lvn.faq.3.a')} data-id="element-2823" />
            <FaqItem question={t('lvn.faq.4.q')} answer={t('lvn.faq.4.a')} data-id="element-2824" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2825">
        <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-2826">
          {t('lvn.cta.title')}
        </h2>
        <p className="text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-2xl mx-auto font-sub" data-id="element-2827">
          {t('lvn.cta.body')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2828">
          <Link to="/#materials" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2829">
            {t('lvn.cta.btnQuote')}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-2830" />
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-8 py-4 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white rounded-xl font-bold text-lg transition-colors" data-id="element-2831">
            <Phone className="w-5 h-5 mr-2" data-id="element-2832" />
            {t('lvn.cta.btnCall')}
          </a>
        </div>
      </section>
    </div>;
}